import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Volume2, VolumeX } from 'lucide-react';
import { useExperience, isWebsite } from '@/store/experience';
import { audio } from '@/lib/audio';
import { scrollToSection } from '@/lib/scroll';

const ITEMS = [
  { id: 'home', label: 'Home' }, { id: 'services', label: 'Services' }, { id: 'websites', label: 'Websites' },
  { id: 'portfolio', label: 'Portfolio' }, { id: 'start', label: 'Start a project', cta: true },
];

export function FloatingNavigation() {
  const phase = useExperience((s) => s.phase);
  const active = useExperience((s) => s.activeSection);
  const scroll = useExperience((s) => s.scroll);
  const soundOn = useExperience((s) => s.soundOn);
  const setSoundOn = useExperience((s) => s.setSoundOn);
  const [lag, setLag] = useState(0);
  const feet = Math.round(14000 - scroll * 11200);

  useEffect(() => { setLag(8); const t = setTimeout(() => setLag(0), 250); return () => clearTimeout(t); }, [scroll]);

  const toggleSound = async () => { const next = !soundOn; setSoundOn(next); await audio.setEnabled(next); audio.setScene('website', 0.6); };

  return (
    <AnimatePresence>
      {isWebsite(phase) && (
        <motion.nav key="nav" className="fixed z-40 left-0 right-0 top-4 sm:top-6 flex justify-center px-3 pointer-events-none" initial={{ y: -60, opacity: 0 }} animate={{ y: lag, opacity: 1 }} transition={{ type: 'spring', stiffness: 60, damping: 16 }} data-testid="floating-nav" aria-label="Floating navigation">
          <div className="glass pointer-events-auto flex items-center gap-1 sm:gap-2 rounded-full pl-2 pr-2 py-1.5 max-w-[calc(100vw-1.5rem)] overflow-x-auto no-scrollbar">
            <button type="button" onClick={() => scrollToSection('home')} className="hidden sm:flex items-center gap-2 pl-2 pr-3" data-testid="nav-logo" aria-label="Home">
              <img src="/assets/logo.png" alt="StorySpark Studios" className="h-7 w-auto" />
              <span className="font-display text-[0.6rem] tracking-[0.25em] text-white/70 whitespace-nowrap" data-testid="nav-altitude">{feet.toLocaleString()} FT</span>
            </button>
            {ITEMS.map((it) => (
              <button key={it.id} type="button" onClick={() => scrollToSection(it.id)} data-testid={`nav-${it.id}`}
                className={`relative whitespace-nowrap rounded-full px-2.5 sm:px-4 py-2 font-display text-[0.58rem] sm:text-[0.68rem] tracking-[0.18em] sm:tracking-[0.22em] uppercase transition-colors duration-300 ${it.cta ? 'bg-[#f97316]/90 text-white hover:bg-[#fb8a3c]' : active === it.id ? 'text-white' : 'text-white/60 hover:text-white'}`}>
                {it.label}
                {!it.cta && active === it.id && <motion.span layoutId="nav-dot" className="absolute -bottom-0.5 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-[#f97316] shadow-[0_0_10px_#f97316]" />}
              </button>
            ))}
            <button type="button" onClick={toggleSound} className="rounded-full p-2 text-white/70 hover:text-white transition-colors" aria-label="Toggle sound" data-testid="nav-sound-toggle">
              {soundOn ? <Volume2 size={15} /> : <VolumeX size={15} />}
            </button>
          </div>
        </motion.nav>
      )}
    </AnimatePresence>
  );
}
